import React, { useState, useEffect, useRef } from "react";
import about1 from "../assets/Screenshot 2025-07-28 at 8.56.39 PM.png";
import about2 from "../assets/venue.jpg";
import about3 from "../assets/decoration.jpeg";

function About() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ weddings: 0, venues: 0, years: 0 });
  const [content, setContent] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  // Load CMS content
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/content/about.json", { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          setContent(data);
        }
      } catch (e) {
        console.warn("Using default about content");
      }
    })();
  }, []);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
          animateCounters();
        }
      },
      { threshold: 0.2, rootMargin: "-50px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Counter animation
  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targetStats = content?.stats || { weddings: 500, venues: 200, years: 10 };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCounters({
        weddings: Math.floor(targetStats.weddings * easeOut),
        venues: Math.floor(targetStats.venues * easeOut),
        years: Math.floor(targetStats.years * easeOut),
      });

      if (step >= steps) clearInterval(timer);
    }, interval);
  };

  // Styles
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  
  const wrapper = {
    background: "linear-gradient(135deg, #faf8f5 0%, #fff5f7 100%)",
    padding: isMobile ? "60px 16px" : isTablet ? "80px 20px" : "100px 20px",
    fontFamily: "'Manrope', sans-serif",
    position: "relative",
    overflow: "hidden",
  };

  const container = {
    display: "grid",
    gridTemplateColumns: (isMobile || isTablet) ? "1fr" : "repeat(2, 1fr)",
    alignItems: "center",
    gap: isMobile ? "40px" : isTablet ? "60px" : "80px",
    maxWidth: "1300px",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
  };

  /* ==== ENHANCED IMAGE COLLAGE ==== */
  const collageWrapper = {
    position: "relative",
    height: isMobile ? "400px" : isTablet ? "500px" : "600px",
    maxWidth: (isMobile || isTablet) ? "600px" : "none",
    margin: (isMobile || isTablet) ? "0 auto" : "0",
    width: "100%",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateX(0)" : "translateX(-50px)",
    transition: "all 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
  };

  const imgBase = {
    position: "absolute",
    borderRadius: isMobile ? "16px" : "24px",
    boxShadow: "0 25px 50px -12px rgba(103, 13, 43, 0.25)",
    objectFit: "cover",
    transition: "all 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
    display: "block",
  };

  const img1 = {
    ...imgBase,
    width: isMobile ? "70%" : "65%",
    height: isMobile ? "50%" : "55%",
    top: "0",
    left: "0",
    zIndex: 1,
  };

  const img2 = {
    ...imgBase,
    width: isMobile ? "65%" : "60%",
    height: isMobile ? "45%" : "50%",
    bottom: "0",
    right: "0",
    zIndex: 2,
  };

  const img3 = {
    ...imgBase,
    width: isMobile ? "60%" : "50%",
    height: isMobile ? "40%" : "45%",
    top: isMobile ? "30%" : "35%",
    left: isMobile ? "20%" : "30%",
    zIndex: 3,
    border: isMobile ? "4px solid #fff" : "6px solid #fff",
    boxShadow: "0 30px 60px -15px rgba(0, 0, 0, 0.3)",
  };

  /* ==== TEXT CONTENT ==== */
  const textBox = {
    textAlign: (isMobile || isTablet) ? "center" : "left",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(30px)",
    transition: "all 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s",
  };

  const sectionLabelStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: (isMobile || isTablet) ? "center" : "flex-start",
    gap: "12px",
    marginBottom: "20px",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s ease 0.3s",
  };

  const line = {
    width: "40px",
    height: "2px",
    background: "#d4af37",
  };

  const labelText = {
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "0.2em",
    color: "#670D2B",
    fontWeight: 600,
  };

  const headingStyle = {
    fontFamily: "'Playfair Display', serif",
    fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
    color: "#670D2B",
    marginBottom: "24px",
    lineHeight: 1.1,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s ease 0.4s",
  };

  const headingAccentStyle = {
    color: "#d4af37",
    fontStyle: "italic",
  };

  const paragraph = {
    fontSize: "1.15rem",
    lineHeight: 1.8,
    color: "#555",
    marginBottom: "40px",
    maxWidth: "540px",
    marginLeft: (isMobile || isTablet) ? "auto" : "0",
    marginRight: (isMobile || isTablet) ? "auto" : "0",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s ease 0.5s",
  };

  /* ==== STATS SECTION ==== */
  const statsWrapper = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
    gap: isMobile ? "12px" : "20px",
    marginBottom: "40px",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s ease 0.6s",
  };

  const statBox = {
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(10px)",
    padding: "24px 16px",
    borderRadius: "16px",
    textAlign: "center",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    transition: "all 0.3s ease",
    cursor: "default",
  };

  const statNumber = {
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    fontWeight: 700,
    color: "#670D2B",
    marginBottom: "4px",
    fontFamily: "'Playfair Display', serif",
  };

  const statLabel = {
    fontSize: "0.9rem",
    color: "#666",
    fontWeight: 500,
  };

  /* ==== FEATURES GRID ==== */
  const featureList = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
    gap: "16px",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s ease 0.7s",
  };

  const featureItem = {
    background: "#fff",
    padding: "20px",
    borderRadius: "14px",
    border: "1px solid rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    cursor: "default",
  };

  const featureIcon = {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #fff5f7 0%, #ffe4e9 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
  };

  const featureText = {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#333",
  };

  const featureSubtext = {
    fontSize: "0.8rem",
    color: "#888",
    marginTop: "2px",
  };

  // Decorative elements
  const decorCircle = {
    position: "absolute",
    borderRadius: "50%",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    animation: "rotate 20s linear infinite",
  };

  const features = content?.features || [
    { icon: "✦", title: "Bespoke Design", sub: "Tailored aesthetics" },
    { icon: "✦", title: "Multi-Day Events", sub: "Extended celebrations" },
    { icon: "✦", title: "Sustainable Luxury", sub: "Eco-conscious elegance" },
    { icon: "✦", title: "Cultural Fusion", sub: "Heritage ceremonies" },
  ];

  const stats = [
    { key: "weddings", value: counters.weddings, suffix: "+", label: "Weddings Planned" },
    { key: "venues", value: counters.venues, suffix: "+", label: "Venues Curated" },
    { key: "years", value: counters.years, suffix: " yrs", label: "Experience" },
  ];

  const heading = content?.heading || "Crafting Timeless";
  const headingAccent = content?.heading_accent || "Celebrations";
  const description = content?.description || "Where dreams transcend into reality. We curate bespoke weddings and luxury events that narrate your unique love story. From royal palaces in Jaipur to intimate beach vows in Goa, every moment is meticulously orchestrated with elegance and intention.";
  const sectionLabel = content?.label || "About Parivar";

  return (
    <section id="about" ref={sectionRef} style={wrapper}>
      {/* Background Decorations */}
      <div
        style={{
          ...decorCircle,
          width: "400px",
          height: "400px",
          top: "-100px",
          right: "-100px",
        }}
      />
      <div
        style={{
          ...decorCircle,
          width: "300px",
          height: "300px",
          bottom: "-50px",
          left: "-50px",
          animationDirection: "reverse",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(212,175,55,0.03) 0%, transparent 70%)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />

      <div style={container}>
        {/* Image Collage */}
        <div style={collageWrapper}>
          <img
            src={about1}
            alt="Wedding Setup"
            style={img1}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.03) translateY(-5px)";
              e.target.style.zIndex = 10;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1) translateY(0)";
              e.target.style.zIndex = 1;
            }}
          />
          <img
            src={about2}
            alt="Luxury Venue"
            style={img2}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.03) translateY(-5px)";
              e.target.style.zIndex = 10;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1) translateY(0)";
              e.target.style.zIndex = 2;
            }}
          />
          <img
            src={about3}
            alt="Event Decoration"
            style={img3}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 40px 80px -20px rgba(0, 0, 0, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "0 30px 60px -15px rgba(0, 0, 0, 0.3)";
            }}
          />
        </div>

        {/* Text Section */}
        <div style={textBox}>
          <div style={sectionLabelStyle}>
            <div style={line} />
            <span style={labelText}>{sectionLabel}</span>
          </div>

          <h2 style={headingStyle}>
            {heading}
            <br />
            <span style={headingAccentStyle}>{headingAccent}</span>
          </h2>

          <p style={paragraph}>
            {description}
          </p>

          {/* Stats Section */}
          <div style={statsWrapper}>
            {stats.map((stat) => (
              <div
                key={stat.key}
                style={statBox}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 20px 40px -15px rgba(103, 13, 43, 0.15)";
                  e.currentTarget.style.borderColor = "rgba(212, 175, 55, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(212, 175, 55, 0.2)";
                }}
              >
                <div style={statNumber}>
                  {stat.value}{stat.suffix}
                </div>
                <div style={statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Feature List */}
          <div style={featureList}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={featureItem}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateX(8px)";
                  e.currentTarget.style.borderColor = "rgba(212, 175, 55, 0.3)";
                  e.currentTarget.style.boxShadow = "0 10px 30px -10px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.05)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={featureIcon}>{feature.icon}</div>
                <div>
                  <div style={featureText}>{feature.title}</div>
                  <div style={featureSubtext}>{feature.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  );
}

export default About;